import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../../firebase-client";
import { BOOKING_FIELD_TYPES, courseEditorConfig, courseMemberTagOptions, courseMoneyInputMinor, courseSessionTimesForStart, courseTicketTimingConfig, hydrateCourseBookingNames, paginateCourseOperations, priceForTicket, repeatCourseSession, resolveCourseTicketTiming } from "../../../../utils/courseBooking";
import EmailComposerModal from "../../../teams/components/modals/communication/emailComposer";
import EditDocumentsForm from "../editDocuments";
import { LocationFields } from "../events/sections/eventModeFields";
import CourseBoatReservations from "./CourseBoatReservations";
import { getRestrictableDashboardCards } from "../../../teams/utils/moduleRestrictions";
import useStripePaymentGuard from "../../../payments/useStripePaymentGuard";

const saveCourse = httpsCallable(functions, "saveGenericCourse");
const saveCourseDraft = httpsCallable(functions, "saveGenericCourseDraft");
const publishForm = httpsCallable(functions, "publishBookingFormVersion");
const saveFormDraft = httpsCallable(functions, "saveBookingFormDraft");
const listForms = httpsCallable(functions, "listBookingForms");
const copyForm = httpsCallable(functions, "copyBookingFormToSource");
const exportBookings = httpsCallable(functions, "exportBookingCsv");
const generateRegister = httpsCallable(functions, "generateSafetyRegister");
const updateAttendance = httpsCallable(functions, "updateCourseAttendance");
const resolveBookingAccountNames = httpsCallable(functions, "resolveCourseBookingAccountNames");
const listDocuments = httpsCallable(functions, "listLinkableBookingDocuments");
const updateDocumentLinks = httpsCallable(
  functions,
  "updateBookingDocumentLinks",
);
const refundBooking = httpsCallable(functions, "refundGenericCourseBooking");
const releaseWaitlistOffer = httpsCallable(
  functions,
  "releaseCourseWaitlistOffer",
);
const uid = (prefix) => `${prefix}_${crypto.randomUUID()}`;
const emptySession = () => ({
  id: uid("session"),
  name: "",
  start: "",
  end: "",
  locationId: "",
  locationName: "",
  locationStreet: "",
  locationCity: "",
  locationPostcode: "",
  locationCountry: "",
  assetReservations: [],
});
const emptyTicket = () => ({
  id: uid("ticket"),
  name: "General admission",
  priceMinor: 0,
  eligibility: "everyone",
  capacity: "",
  memberTagNames: [],
  priceSchedule: [],
});
const emptyExtra = () => ({
  id: uid("extra"),
  name: "",
  priceMinor: 0,
  scope: "attendee",
  capacity: "",
  maxQuantity: 1,
});
const emptyField = () => ({
  key: uid("field"),
  label: "",
  helpText: "",
  type: "text",
  scope: "attendee",
  required: false,
  sensitive: false,
  options: [],
  declarationVersion: "1",
  declarationWording: "",
  profileBinding: "",
});
const emptyCourse = () => ({
  name: "",
  description: "",
  status: "draft",
  audience: "everyone",
  capacity: 12,
  currency: "GBP",
  sessions: [emptySession()],
  ticketTypes: [],
  extras: [],
  waitlist: { enabled: false, offerHours: 48 },
  bookingForm: null,
  bookingFormMode: "",
  ticketingReviewed: false,
  protectedAnswerRetentionDays: 30,
  restrictActiveCourseCards: false,
  activeCourseVisibleCardIds: [],
  completionTagNames: [],
  conversion: {
    enabled: false,
    startsMode: "after_final_session",
    startsAt: "",
    pricingMode: "full_price",
  },
});

const pounds = (minor) => (Number(minor || 0) / 100).toFixed(2);
const COURSE_OPERATIONS_PAGE_SIZE = 10;

function CourseOperationsPager({ label, total, pagination, onPageChange }) {
  if (total <= COURSE_OPERATIONS_PAGE_SIZE) return null;
  return <nav className="course-operations-pager" aria-label={`${label} pages`}>
    <span>Showing {pagination.start + 1}–{Math.min(pagination.start + COURSE_OPERATIONS_PAGE_SIZE, total)} of {total}</span>
    <div>
      <button type="button" className="button button-secondary" disabled={pagination.page === 1} onClick={() => onPageChange(pagination.page - 1)}>Previous</button>
      <span>Page {pagination.page} of {pagination.pageCount}</span>
      <button type="button" className="button button-secondary" disabled={pagination.page === pagination.pageCount} onClick={() => onPageChange(pagination.page + 1)}>Next</button>
    </div>
  </nav>;
}

function TagPicker({
  tags,
  selected = [],
  onChange,
  label = "Tags",
  placeholder = "Search existing club tags",
  emptyText = "No tags selected.",
  resultsLabel = "Matching tags",
}) {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const term = search.trim().toLocaleLowerCase();
  const matches = useMemo(() => term ? tags.filter((tag) => `${tag.name} ${tag.category}`.toLocaleLowerCase().includes(term)).slice(0, 12) : [], [tags, term]);
  const toggle = (name) => onChange(selectedSet.has(name) ? selected.filter((tag) => tag !== name) : [...selected, name]);
  return <div className="course-member-tag-picker">
    <label>{label}<input className="input" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={placeholder} /></label>
    {selected.length > 0 && <div className="course-member-tag-picker__selected" aria-label={`Selected ${label.toLocaleLowerCase()}`}>{selected.map((name) => <button key={name} type="button" onClick={() => toggle(name)} title={`Remove ${name}`}>{name}<span aria-hidden="true">×</span></button>)}</div>}
    {selected.length === 0 && <p className="text-muted">{emptyText}</p>}
    {term && <div className="course-member-tag-picker__results" role="group" aria-label={resultsLabel}>{matches.length > 0 ? matches.map((tag) => <label key={tag.name}><input type="checkbox" checked={selectedSet.has(tag.name)} onChange={() => toggle(tag.name)} /><span>{tag.name}<small>{tag.category}</small></span></label>) : <p className="text-muted">No existing tags match “{search.trim()}”.</p>}</div>}
  </div>;
}

function DashboardCardPicker({ selected = [], onChange, metadata = {} }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const optionalCards = useMemo(
    () => getRestrictableDashboardCards(metadata).filter((card) => card.id !== "courses"),
    [metadata],
  );
  const toggle = (id) => onChange(selectedSet.has(id) ? selected.filter((cardId) => cardId !== id) : [...selected, id]);
  return <div className="course-card-picker">
    <div className="course-card-picker__heading">
      <div>
        <strong>Club modules available while enrolled</strong>
        <p className="text-muted">Courses and Manage Membership remain available. Select any additional club areas external participants may use.</p>
      </div>
      <div className="course-card-picker__actions">
        <button type="button" className="btn btn--secondary btn--xs" onClick={() => onChange(optionalCards.map((card) => card.id))}>Select all</button>
        <button type="button" className="btn btn--secondary btn--xs" onClick={() => onChange([])}>Clear optional</button>
      </div>
    </div>
    <div className="course-card-picker__grid" role="group" aria-label="Dashboard cards visible while enrolled">
      <label className="course-card-picker__option course-card-picker__option--locked">
        <input type="checkbox" checked disabled />
        <span>Manage Membership<small>Always visible</small></span>
      </label>
      <label className="course-card-picker__option course-card-picker__option--locked">
        <input type="checkbox" checked disabled />
        <span>Courses<small>Always visible</small></span>
      </label>
      {optionalCards.map((card) => <label className="course-card-picker__option" key={card.id}>
        <input type="checkbox" checked={selectedSet.has(card.id)} onChange={() => toggle(card.id)} />
        <span>{card.label}</span>
      </label>)}
    </div>
  </div>;
}

export default function EditCoursesForm({
  teamId,
  focusCourseId = "",
  createNew = false,
  canManageBookingForms = false,
  teamMetadata = {},
  onUnsavedChangesChange,
  onClose,
}) {
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [liveStatus, setLiveStatus] = useState("draft");
  const [draft, setDraft] = useState(emptyCourse());
  const [forms, setForms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [teamTags, setTeamTags] = useState([]);
  const [sessionCopyOptions, setSessionCopyOptions] = useState({});
  const [openSessionCopyId, setOpenSessionCopyId] = useState("");
  const [moneyInputs, setMoneyInputs] = useState({});
  const [paidEditingReady, setPaidEditingReady] = useState(false);
  const [formDraft, setFormDraft] = useState({
    name: "",
    description: "",
    fields: [],
  });
  const [formDraftId, setFormDraftId] = useState("");
  const [canEditForms, setCanEditForms] = useState(canManageBookingForms);
  const [copySourceFormId, setCopySourceFormId] = useState("");
  const [participants, setParticipants] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [operationsSearch, setOperationsSearch] = useState("");
  const [attendancePage, setAttendancePage] = useState(1);
  const [bookingPage, setBookingPage] = useState(1);
  const [waitlistPage, setWaitlistPage] = useState(1);
  const [attendanceSessionId, setAttendanceSessionId] = useState("");
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [registerLayout, setRegisterLayout] = useState("compact");
  const [exportColumns, setExportColumns] = useState(
    "sourceName, occurrence, attendee, bookingContact, ticketType, quantity, extras, paymentStatus, refundStatus, bookingDate",
  );
  const [documents, setDocuments] = useState([]);
  const [linkedDocumentIds, setLinkedDocumentIds] = useState([]);
  const [tab, setTab] = useState("details");
  const [workspaceView, setWorkspaceView] = useState(focusCourseId ? "editor" : createNew ? "create" : "list");
  const [courseSearch, setCourseSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [copyCourseId, setCopyCourseId] = useState("");
  const [baseline, setBaseline] = useState("");
  const [formBaseline, setFormBaseline] = useState("");
  const [formNeedsPublication, setFormNeedsPublication] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [isEditingField, setIsEditingField] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState("saved");
  const [message, setMessage] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [showDocumentManager, setShowDocumentManager] = useState(false);
  const [documentRefresh, setDocumentRefresh] = useState(0);
  const courseIdRef = useRef("");
  const formDraftIdRef = useRef("");
  const latestSnapshotRef = useRef("");
  const saveDraftRef = useRef(null);
  const snapshotState = (course, form) => JSON.stringify({ course, form });
  const setEditorState = (selected, availableForms = forms) => {
    const nextDraft = selected ? courseEditorConfig(selected, emptyCourse()) : emptyCourse();
    const owned = selected ? availableForms.find((item) => item.ownerType === "course" && item.ownerId === selected.id) : null;
    const nextForm = owned?.draft || { name: `${nextDraft.name || "Course"} booking form`, description: "", fields: [] };
    setCourseId(selected?.id || "");
    setLiveStatus(selected?.status || "draft");
    setDraft(nextDraft);
    setFormDraftId(owned?.id || "");
    setFormDraft(nextForm);
    setFormNeedsPublication(owned?.draftNeedsPublishing === true);
    setMoneyInputs({});
    setCopySourceFormId("");
    setOperationsSearch("");
    setAttendancePage(1);
    setBookingPage(1);
    setWaitlistPage(1);
    setTab("details");
    setBaseline(snapshotState(nextDraft, nextForm));
    setFormBaseline(JSON.stringify(nextForm));
    setWorkspaceView("editor");
  };
  const load = async ({ initialise = false } = {}) => {
    if (initialise) setLoadingCourses(true);
    const formPromise = listForms({ teamId }).catch(() => ({ data: { forms: [] } }));
    const locationPromise = getDocs(query(collection(db, "teams", teamId, "locations"), orderBy("name"))).catch(() => null);
    const teamPromise = getDoc(doc(db, "teams", teamId)).catch(() => null);
    let courseSnap;
    try {
      courseSnap = await getDocs(
        query(collection(db, "teams", teamId, "courses"), orderBy("name")),
      );
    } catch (error) {
      setLoadingCourses(false);
      throw error;
    }
    const courseAdmin = await Promise.all(courseSnap.docs.map(async (courseDoc) => {
      const [participantsSnapshot, draftSnapshot] = await Promise.all([
        getDocs(collection(db, "teams", teamId, "courses", courseDoc.id, "participants")).catch(() => null),
        getDoc(doc(db, "teams", teamId, "courses", courseDoc.id, "adminData", "editorDraft")).catch(() => null),
      ]);
      return {
        activeParticipantCount: participantsSnapshot?.docs.filter((participant) => !["refunded", "cancelled", "purged"].includes(participant.data()?.status)).length || 0,
        editorDraft: draftSnapshot?.data()?.config || null,
      };
    }));
    const [formResult, locationSnap, teamSnap] = await Promise.all([
      formPromise,
      locationPromise,
      teamPromise,
    ]);
    const next = courseSnap.docs.map((doc, index) => ({ id: doc.id, ...doc.data(), ...courseAdmin[index] }));
    const nextForms = formResult.data?.forms || [];
    setCourses(next);
    setForms(nextForms);
    setLocations(locationSnap?.docs.map((location) => ({ id: location.id, ...location.data() })) || []);
    setTeamTags(courseMemberTagOptions(teamSnap?.data()?.tags || []));
    setCanEditForms(canManageBookingForms || formResult.data?.canManageForms === true);
    if (initialise) {
      const selected = next.find((course) => course.id === focusCourseId) || null;
      if (selected) setEditorState(selected, nextForms);
      else if (createNew) setWorkspaceView("create");
      else setWorkspaceView("list");
    }
    setLoadingCourses(false);
  };
  useEffect(() => {
    load({ initialise: true }).catch((error) => {
      setLoadingCourses(false);
      setWorkspaceView("list");
      setMessage(error.message);
    });
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!courseId) {
      setParticipants([]);
      setBookings([]);
      setWaitlist([]);
      return;
    }
    let active = true;
    Promise.all([
      getDocs(
        collection(db, "teams", teamId, "courses", courseId, "participants"),
      ),
      getDocs(collection(db, "teams", teamId, "courses", courseId, "bookings")),
      getDocs(collection(db, "teams", teamId, "courses", courseId, "waitlist")),
    ])
      .then(async ([participantSnapshot, bookingSnapshot, waitlistSnapshot]) => {
        const participantRows = participantSnapshot.docs.map((participantDoc) => ({
          id: participantDoc.id,
          ...participantDoc.data(),
        }));
        const bookingRows = bookingSnapshot.docs.map((bookingDoc) => ({ id: bookingDoc.id, ...bookingDoc.data() }));
        const waitlistRows = waitlistSnapshot.docs
          .map((waitlistDoc) => ({ id: waitlistDoc.id, ...waitlistDoc.data() }))
          .sort((left, right) => Number(left.requestedAt?.toMillis?.() || 0) - Number(right.requestedAt?.toMillis?.() || 0));
        const accountIds = Array.from(new Set([
          ...participantRows.map((participant) => participant.id),
          ...bookingRows.flatMap((booking) => [
            booking.purchaserAccountId,
            ...(booking.attendees || []).map((attendee) => attendee.accountId),
          ]),
          ...waitlistRows.flatMap((entry) => [entry.accountId, entry.purchaserAccountId, entry.id]),
        ].filter(Boolean)));
        const localAccountNames = await Promise.all(accountIds.map(async (accountId) => {
          const account = await getDoc(doc(db, "users", accountId)).catch(() => null);
          const data = account?.data?.() || {};
          return [
            [data.firstName, data.lastName].filter(Boolean).join(" "),
            data.displayName,
            data.name,
            data.username,
            data.email,
          ].find((value) => typeof value === "string" && value.trim()) || "";
        }));
        const resolvedResult = await resolveBookingAccountNames({ teamId, courseId, accountIds })
          .catch(() => ({ data: { names: {} } }));
        const namesByAccountId = Object.fromEntries(accountIds.map((accountId, index) => [
          accountId,
          resolvedResult.data?.names?.[accountId] || localAccountNames[index] || "",
        ]));
        if (!active) return;
        setParticipants(participantRows.map((participant) => ({
          ...participant,
          attendeeName: namesByAccountId[participant.id]
            || (participant.attendeeName !== "Course participant" ? participant.attendeeName : "")
            || "Participant (name unavailable)",
        })));
        setBookings(hydrateCourseBookingNames(bookingRows, namesByAccountId));
        setWaitlist(waitlistRows.map((entry) => ({
          ...entry,
          accountName: namesByAccountId[entry.accountId || entry.purchaserAccountId || entry.id] || "Waitlisted account",
        })));
      })
      .catch(() => {
        if (!active) return;
        setParticipants([]);
        setBookings([]);
        setWaitlist([]);
      });
    return () => {
      active = false;
    };
  }, [courseId, teamId]);
  useEffect(() => {
    listDocuments({ teamId, sourceType: "course" })
      .then((result) => {
        const rows = result.data?.documents || [];
        setDocuments(rows);
        setLinkedDocumentIds(
          rows
            .filter((document) => document.linkedCourseIds?.includes(courseId))
            .map((document) => document.id),
        );
      })
      .catch(() => {
        setDocuments([]);
        setLinkedDocumentIds([]);
      });
  }, [courseId, teamId, documentRefresh]);
  const patch = (key, value) =>
    setDraft((current) => ({
      ...current,
      ...(key === "ticketTypes" ? { ticketingReviewed: false } : {}),
      [key]: value,
    }));
  const patchList = (key, index, next) =>
    patch(
      key,
      draft[key].map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...next } : item,
      ),
    );
  const patchSessionLocation = (index, event) => {
    const { name, value } = event.target;
    if (name === "locationId") {
      const selected = locations.find((location) => location.id === value);
      patchList("sessions", index, {
        locationId: value,
        locationName: selected?.name || "",
        locationStreet: selected?.street || "",
        locationCity: selected?.city || "",
        locationPostcode: selected?.postcode || "",
        locationCountry: selected?.country || "",
      });
      return;
    }
    patchList("sessions", index, { locationId: "", [name]: value });
  };
  const sessionCopyOption = (sessionId) => sessionCopyOptions[sessionId] || { count: 1, every: 1, unit: "weeks" };
  const patchSessionCopyOption = (sessionId, next) => setSessionCopyOptions((current) => ({
    ...current,
    [sessionId]: { ...sessionCopyOption(sessionId), ...next },
  }));
  const copySession = (index) => {
    const session = draft.sessions[index];
    const option = sessionCopyOption(session.id);
    const copies = repeatCourseSession(session, {
      ...option,
      makeId: () => uid("session"),
    });
    if (!copies.length) return;
    patch("sessions", [
      ...draft.sessions.slice(0, index + 1),
      ...copies,
      ...draft.sessions.slice(index + 1),
    ]);
    setOpenSessionCopyId("");
  };
  const patchTicketTiming = (index, next) => {
    const ticket = draft.ticketTypes[index];
    const current = courseTicketTimingConfig(ticket);
    const updated = {
      ...ticket,
      ...(next.standardPriceMinor == null ? {} : { standardPriceMinor: next.standardPriceMinor }),
      availability: { ...current.availability, ...(next.availability || {}) },
      earlyBird: { ...current.earlyBird, ...(next.earlyBird || {}) },
    };
    patchList("ticketTypes", index, resolveCourseTicketTiming(updated, draft.sessions));
  };
  const moneyInputValue = (key, minorValue) => Object.prototype.hasOwnProperty.call(moneyInputs, key)
    ? moneyInputs[key]
    : pounds(minorValue);
  const updateMoneyInput = (key, value, onValidMinor) => {
    setMoneyInputs((current) => ({ ...current, [key]: value }));
    const parsed = courseMoneyInputMinor(value);
    if (parsed == null) return;
    if (parsed > 0 && !paidEditingReady) {
      requireStripePayment({
        feature: 'generic_courses',
        featureLabel: 'Paid courses',
        returnTarget: 'generic_courses',
        resumeEntity: courseId || 'new',
        onReady: () => {
          setPaidEditingReady(true);
          onValidMinor(parsed);
        },
      });
      return;
    }
    onValidMinor(parsed);
  };
  const finishMoneyInput = (key) => setMoneyInputs((current) => {
    const next = { ...current };
    delete next[key];
    return next;
  });
  const resolvedCourseConfig = (course) => ({
    ...course,
    ticketTypes: (course.ticketTypes || []).map((ticket) => resolveCourseTicketTiming(ticket, course.sessions || [])),
  });
  const selectedForm = useMemo(() => forms.find((form) => form.ownerType === "course" && form.ownerId === courseId) || forms.find((form) => form.id === draft.bookingForm?.formId), [forms, courseId, draft.bookingForm]);
  const currentSnapshot = useMemo(() => snapshotState(draft, formDraft), [draft, formDraft]);
  const isDirty = useMemo(() => baseline !== currentSnapshot, [baseline, currentSnapshot]);
  const formIsDirty = useMemo(() => formBaseline !== JSON.stringify(formDraft), [formBaseline, formDraft]);
  courseIdRef.current = courseId;
  formDraftIdRef.current = formDraftId;
  latestSnapshotRef.current = currentSnapshot;
  useEffect(() => { onUnsavedChangesChange?.(isDirty); return () => onUnsavedChangesChange?.(false); }, [isDirty, onUnsavedChangesChange]);
  const copySources = useMemo(() => forms.filter((form) => form.id !== formDraftId), [forms, formDraftId]);
  const filteredCourses = useMemo(() => courses.filter((course) => {
    const matchesStatus = statusFilter === "all" || course.status === statusFilter;
    const term = courseSearch.trim().toLowerCase();
    return matchesStatus && (!term || `${course.name || ""} ${course.description || ""}`.toLowerCase().includes(term));
  }), [courses, courseSearch, statusFilter]);
  const confirmedParticipants = useMemo(
    () => participants.filter((participant) => !["refunded", "cancelled", "purged"].includes(participant.status)),
    [participants],
  );
  const attendanceSession = useMemo(
    () => draft.sessions.find((session) => session.id === attendanceSessionId) || draft.sessions[0] || null,
    [attendanceSessionId, draft.sessions],
  );
  const completedBookings = useMemo(
    () => bookings.filter((booking) => ["paid", "partially_refunded", "refunded"].includes(booking.status)),
    [bookings],
  );
  const operationsTerm = operationsSearch.trim().toLocaleLowerCase();
  const matchingParticipants = useMemo(() => confirmedParticipants.filter((participant) => !operationsTerm
    || `${participant.attendeeName || ""} ${participant.ticketName || ""}`.toLocaleLowerCase().includes(operationsTerm)), [confirmedParticipants, operationsTerm]);
  const matchingBookings = useMemo(() => completedBookings.filter((booking) => !operationsTerm
    || `${booking.bookingContactName || ""} ${booking.status || ""} ${(booking.attendees || []).map((attendee) => `${attendee.name || ""} ${attendee.ticketName || ""}`).join(" ")}`.toLocaleLowerCase().includes(operationsTerm)), [completedBookings, operationsTerm]);
  const activeWaitlist = useMemo(() => waitlist.filter((entry) => ["queued", "offered"].includes(entry.status)), [waitlist]);
  const matchingWaitlist = useMemo(() => activeWaitlist.filter((entry) => !operationsTerm
    || `${entry.accountName || ""} ${entry.status || ""}`.toLocaleLowerCase().includes(operationsTerm)), [activeWaitlist, operationsTerm]);
  const attendancePagination = useMemo(() => paginateCourseOperations(matchingParticipants, attendancePage), [attendancePage, matchingParticipants]);
  const bookingPagination = useMemo(() => paginateCourseOperations(matchingBookings, bookingPage), [bookingPage, matchingBookings]);
  const waitlistPagination = useMemo(() => paginateCourseOperations(matchingWaitlist, waitlistPage), [matchingWaitlist, waitlistPage]);
  const activePaymentHolds = useMemo(
    () => bookings.filter((booking) => {
      if (booking.status !== "pending") return false;
      const expiresAt = Number(booking.holdExpiresAt?.toMillis?.() || new Date(booking.holdExpiresAt || 0).getTime());
      return expiresAt > Date.now();
    }),
    [bookings],
  );
  const activePaymentHoldPlaces = useMemo(
    () => activePaymentHolds.reduce((total, booking) => total + Number(booking.activeAttendeeCount || booking.attendees?.length || 0), 0),
    [activePaymentHolds],
  );
  const confirmDiscard = () => !isDirty || window.confirm("Discard your unsaved course changes?");
  const returnToList = () => { if (!confirmDiscard()) return; setBaseline(snapshotState(draft, formDraft)); setWorkspaceView("list"); };
  const startCopiedCourse = () => {
    const source = courses.find((course) => course.id === copyCourseId);
    if (!source) return;
    const sourceDraft = courseEditorConfig(source, emptyCourse());
    const sourceForm = forms.find((form) =>
      (form.ownerType === "course" && form.ownerId === source.id)
      || form.id === sourceDraft.bookingForm?.formId
    );
    const sourceFields = sourceForm?.draft?.fields || [];
    const canCopyQuestions = canEditForms && sourceFields.length > 0;
    const copied = {
      ...emptyCourse(),
      name: `Copy of ${sourceDraft.name || "course"}`,
      description: sourceDraft.description || "",
      audience: sourceDraft.audience || "everyone",
      capacity: sourceDraft.capacity || 12,
      currency: sourceDraft.currency || "GBP",
      sessions: (sourceDraft.sessions || []).map((session) => ({ ...session, id: uid("session"), assetReservations: [] })),
      ticketTypes: (sourceDraft.ticketTypes || []).map((ticket) => ({
        ...ticket,
        id: uid("ticket"),
        memberTagNames: [...(ticket.memberTagNames || [])],
        priceSchedule: (ticket.priceSchedule || []).map((price) => ({ ...price })),
      })),
      extras: (sourceDraft.extras || []).map((extra) => ({ ...extra, id: uid("extra") })),
      waitlist: { ...emptyCourse().waitlist, ...(sourceDraft.waitlist || {}) },
      bookingForm: null,
      bookingFormMode: sourceDraft.bookingFormMode === "none" ? "none" : canCopyQuestions ? "questions" : "",
      ticketingReviewed: false,
      protectedAnswerRetentionDays: sourceDraft.protectedAnswerRetentionDays || 30,
      restrictActiveCourseCards: sourceDraft.restrictActiveCourseCards === true,
      activeCourseVisibleCardIds: [...(sourceDraft.activeCourseVisibleCardIds || [])],
      completionTagNames: [...(sourceDraft.completionTagNames || [])],
      conversion: { ...emptyCourse().conversion, ...(sourceDraft.conversion || {}) },
    };
    const copiedForm = canCopyQuestions ? {
      name: `${copied.name} booking form`,
      description: sourceForm?.draft?.description || "",
      fields: sourceFields.map((field) => ({ ...field, options: [...(field.options || [])] })),
    } : { name: `${copied.name} booking form`, description: "", fields: [] };
    setCourseId("");
    courseIdRef.current = "";
    setLiveStatus("draft");
    setDraft(copied);
    setFormDraftId("");
    formDraftIdRef.current = "";
    setFormDraft(copiedForm);
    setFormNeedsPublication(canCopyQuestions);
    setMoneyInputs({});
    setTab("details");
    setBaseline("");
    setFormBaseline("");
    setWorkspaceView("editor");
  };
  const ensureDraftCourse = async (course = draft) => {
    const result = await saveCourseDraft({ teamId, courseId: courseIdRef.current || null, course: resolvedCourseConfig(course) });
    if (!courseIdRef.current) {
      courseIdRef.current = result.data.courseId;
      setCourseId(result.data.courseId);
    }
    return result.data.courseId;
  };
  const publishedSessionTimes = useMemo(() => new Map(
    liveStatus === "published"
      ? (courses.find((course) => course.id === courseId)?.sessions || []).map((session) => [session.id, new Date(session.start).getTime()])
      : [],
  ), [courseId, courses, liveStatus]);
  const validationErrors = useMemo(() => {
    const errors = {};
    if (!draft.name?.trim() || Number(draft.capacity) < 1 || !draft.currency?.trim()) errors.details = "Add a course name, capacity and currency.";
    if (!draft.sessions?.length || draft.sessions.some((session) => !session.name?.trim() || !session.start || !session.end || new Date(session.end) <= new Date(session.start))) {
      errors.sessions = "Every session needs a name and valid start/end time.";
    } else if (draft.sessions.some((session) => (session.assetReservations || []).some((reservation) => !reservation.boatId || !reservation.responsibleMemberId))) {
      errors.sessions = "Choose a boat and qualified responsible member for every reservation.";
    } else if (draft.sessions.some((session) => {
      const startTime = new Date(session.start).getTime();
      return startTime <= Date.now() && publishedSessionTimes.get(session.id) !== startTime;
    })) {
      errors.sessions = "Move every new or changed session into the future before publishing.";
    }
    const invalidTicket = !draft.ticketTypes?.length || draft.ticketTypes.some((ticket) => {
      const timing = courseTicketTimingConfig(ticket);
      const resolved = resolveCourseTicketTiming(ticket, draft.sessions);
      const invalidSpecificDate = (timing.availability.openMode === "specific" && !timing.availability.openAt)
        || (timing.availability.closeMode === "specific" && !timing.availability.closeAt)
        || (timing.earlyBird.mode === "specific" && !timing.earlyBird.endsAt);
      const invalidEarlyBird = timing.earlyBird.mode !== "none" && timing.earlyBird.priceMinor >= timing.standardPriceMinor;
      const invalidWindow = resolved.availableFrom && resolved.availableUntil && new Date(resolved.availableUntil) <= new Date(resolved.availableFrom);
      return !ticket.name?.trim() || Number(timing.standardPriceMinor) < 0 || invalidSpecificDate || invalidEarlyBird || invalidWindow;
    });
    const invalidMoneyInput = Object.values(moneyInputs).some((value) => courseMoneyInputMinor(value) == null);
    if (invalidTicket || invalidMoneyInput) errors.pricing = "Add and complete at least one ticket type. Check each ticket name, booking window and price; early-bird prices must be lower than the standard price.";
    else if (draft.ticketingReviewed !== true) errors.pricing = "Review the ticket prices, eligibility, capacity and booking windows before publishing.";
    const badField = formDraft.fields?.find((field) => !field.label?.trim() || (["single_choice", "multiple_choice"].includes(field.type) && !field.options?.length) || (field.type === "confirmation" && !field.declarationWording?.trim()));
    if (!["questions", "none"].includes(draft.bookingFormMode)) errors.booking = "Choose whether this course needs booking questions and declarations.";
    else if (draft.bookingFormMode === "questions" && !formDraft.fields?.length) errors.booking = "Add at least one booking question or explicitly choose that no additional questions are needed.";
    else if (badField) errors.booking = "Complete every booking question, choice list and declaration wording.";
    return errors;
  }, [draft, formDraft, moneyInputs, publishedSessionTimes]);
  const saveDraft = async ({ automatic = false } = {}) => {
    if (saving) return;
    const courseSnapshot = draft;
    const formSnapshot = formDraft;
    const savedSnapshot = snapshotState(courseSnapshot, formSnapshot);
    setSaving(true);
    setAutosaveStatus("saving");
    if (!automatic) setMessage("");
    try {
      const nextId = await ensureDraftCourse(courseSnapshot);
      const formChanged = JSON.stringify(formSnapshot) !== formBaseline;
      if (canEditForms && (formSnapshot.fields?.length || formDraftIdRef.current || formChanged)) {
        const formResult = await saveFormDraft({ teamId, formId: formDraftIdRef.current || null, form: formSnapshot, ownerType: "course", ownerId: nextId });
        formDraftIdRef.current = formResult.data.formId;
        setFormDraftId(formResult.data.formId);
        setFormNeedsPublication(true);
      }
      if (!automatic) await load();
      if (latestSnapshotRef.current === savedSnapshot) {
        setBaseline(savedSnapshot);
        setFormBaseline(JSON.stringify(formSnapshot));
      }
      setAutosaveStatus("saved");
      if (!automatic) setMessage("Draft saved. The live course has not changed.");
    } catch (error) {
      setAutosaveStatus("error");
      setMessage(automatic ? `Draft autosave failed: ${error.message}` : error.message);
    } finally {
      setSaving(false);
    }
  };
  saveDraftRef.current = saveDraft;
  useEffect(() => {
    if (workspaceView !== "editor" || !isDirty || saving || isEditingField) return undefined;
    setAutosaveStatus("pending");
    const timer = window.setTimeout(() => {
      saveDraftRef.current?.({ automatic: true });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [currentSnapshot, isDirty, isEditingField, saving, workspaceView]);
  const publishCourseNow = async () => {
    const firstError = ["details", "sessions", "pricing", "booking"].find((section) => validationErrors[section]);
    if (firstError) { setTab(firstError); setMessage(validationErrors[firstError]); return; }
    setSaving(true);
    setMessage("");
    try {
      const nextId = await ensureDraftCourse();
      const nextStatus = ["completed", "cancelled"].includes(draft.status) ? draft.status : "published";
      let nextCourse = resolvedCourseConfig({ ...draft, status: nextStatus });
      if (draft.bookingFormMode === "none") nextCourse = { ...nextCourse, bookingForm: null };
      if (draft.bookingFormMode === "questions" && canEditForms && (formIsDirty || formNeedsPublication || (!draft.bookingForm && (formDraft.fields?.length || formDraftId)))) {
        const formResult = await publishForm({ teamId, formId: formDraftId || null, form: formDraft, ownerType: "course", ownerId: nextId });
        setFormDraftId(formResult.data.formId);
        nextCourse = { ...nextCourse, bookingForm: { formId: formResult.data.formId, versionId: formResult.data.versionId } };
        setFormNeedsPublication(false);
        setFormBaseline(JSON.stringify(formDraft));
      }
      setDraft(nextCourse);
      const result = await saveCourse({ teamId, courseId: nextId, course: nextCourse });
      const published = { ...nextCourse, hasUnpublishedChanges: false };
      setCourseId(result.data.courseId);
      setLiveStatus(nextStatus);
      setDraft(published);
      await load();
      setBaseline(snapshotState(published, formDraft));
      setFormBaseline(JSON.stringify(formDraft));
      setMessage(courseId ? "Course update published." : "Course published.");
    } catch (error) {
      setMessage(`Course wasn’t published: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  const publishCourse = async () => {
    const resolvedTickets = (draft.ticketTypes || []).map((ticket) => resolveCourseTicketTiming(ticket, draft.sessions || []));
    const requiresPayment = resolvedTickets.some((ticket) => (
      Number(ticket.standardPriceMinor || ticket.priceMinor || 0) > 0
      || Number(ticket.earlyBird?.priceMinor || 0) > 0
    )) || (draft.extras || []).some((extra) => Number(extra.priceMinor || 0) > 0);
    if (!requiresPayment || paidEditingReady) {
      await publishCourseNow();
      return;
    }
    await requireStripePayment({
      feature: 'generic_courses',
      featureLabel: 'Paid courses',
      returnTarget: 'generic_courses',
      resumeEntity: courseId || 'new',
      onReady: () => {
        setPaidEditingReady(true);
        publishCourseNow();
      },
    });
  };
  const copySelectedForm = async () => {
    if (!copySourceFormId) return;
    setSaving(true);
    try {
      const nextId = await ensureDraftCourse();
      const result = await copyForm({ teamId, sourceFormId: copySourceFormId, ownerType: "course", ownerId: nextId, name: `${draft.name || "Course"} booking form` });
      setFormDraftId(result.data.formId);
      setFormDraft(result.data.form);
      setFormNeedsPublication(true);
      await load();
      setMessage("Form copied into this course. Future changes are independent.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };
  const downloadCsv = async () => {
    const result = await exportBookings({
      teamId,
      sourceType: "course",
      sourceId: courseId,
      includeSensitive,
      includeCancelled,
      columns: exportColumns
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    });
    const blob = new Blob([result.data.csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = result.data.filename;
    link.click();
    URL.revokeObjectURL(url);
  };
  const printRegister = async () => {
    const popup = window.open("", "_blank");
    if (!popup) throw new Error("Allow pop-ups to print the register.");
    popup.opener = null;
    popup.document.write(
      '<!doctype html><html><head><title>Preparing register…</title></head><body><p style="font-family:Arial,sans-serif;padding:24px">Preparing the safety register…</p></body></html>',
    );
    popup.document.close();
    const escape = (value) =>
      String(value ?? "").replace(
        /[&<>"']/g,
        (char) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[char],
      );
    try {
      const result = await generateRegister({
        teamId,
        sourceType: "course",
        sourceId: courseId,
        includeSensitive,
        layout: registerLayout,
        includeCancelled,
      });
      const register = result.data;
      const pages = register.rows.length
        ? register.rows.map((row) => {
          const emergencyContacts = (row.emergencyContacts || []).map((contact) => {
            const detail = [contact.name, contact.relationship, contact.phone].filter(Boolean).join(" · ");
            return `<li>${escape(detail)}</li>`;
          }).join("");
          return `<section class="participant"><h2>${escape(row.attendee)}</h2><p>Booking contact: ${escape(row.bookingContact)}</p><p>Ticket: ${escape(row.ticketType)}</p><p>Status: ${escape(row.paymentStatus)}</p>${emergencyContacts ? `<h3>Emergency contacts</h3><ul>${emergencyContacts}</ul>` : ""}${row.safetyAnswers && Object.keys(row.safetyAnswers).length ? `<h3>Safety information</h3><pre>${escape(JSON.stringify(row.safetyAnswers, null, 2))}</pre>` : ""}</section>`;
        }).join("")
        : '<p class="empty">No confirmed participants match this register.</p>';
      popup.document.open();
      popup.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escape(register.title)}</title><style>@page{size:A4;margin:12mm}body{font-family:Arial,sans-serif;color:#111;margin:0}.meta{border-bottom:2px solid #111;margin-bottom:12px;padding-bottom:10px}.meta h1{margin:0 0 6px}.participant{padding:10px 0;border-bottom:1px solid #bbb}.participant h2{margin:0 0 6px}.participant p{margin:4px 0}.participant_pages .participant{break-after:page;border:0}pre{white-space:pre-wrap}.empty{padding:18px;border:1px solid #bbb}</style></head><body class="${escape(register.layout)}"><div class="meta"><h1>${escape(register.title)}</h1><p>${escape(register.occurrence)} · Generated ${escape(new Date(register.generatedAt).toLocaleString())}</p><strong>${escape(register.confidentiality)}</strong></div>${pages}</body></html>`,
      );
      popup.document.close();
      window.setTimeout(() => {
        popup.focus();
        popup.print();
      }, 150);
    } catch (error) {
      popup.close();
      throw error;
    }
  };
  const saveDocumentLinks = async () => {
    await updateDocumentLinks({
      teamId,
      sourceType: "course",
      sourceId: courseId,
      documentIds: linkedDocumentIds,
    });
    setMessage(
      "Course document links saved. Removing a link does not delete the document.",
    );
  };
  const runRefund = async (booking, selection, label) => {
    if (
      !window.confirm(
        `Refund ${label}? Stripe and the retained course credit will be updated.`,
      )
    )
      return;
    const result = await refundBooking({
      teamId,
      courseId,
      bookingId: booking.id,
      selection,
    });
    const refundedAttendeeIds = new Set(
      selection.type === "extra"
        ? []
        : selection.type === "attendee"
          ? [selection.attendeeAccountId]
          : (booking.attendees || []).map((attendee) => attendee.accountId),
    );
    if (refundedAttendeeIds.size > 0) {
      setParticipants((rows) =>
        rows.map((participant) =>
          refundedAttendeeIds.has(participant.id)
            ? { ...participant, status: "refunded" }
            : participant,
        ),
      );
      setCourses((rows) =>
        rows.map((course) =>
          course.id === courseId
            ? {
                ...course,
                activeParticipantCount: Math.max(
                  0,
                  Number(course.activeParticipantCount || 0) - refundedAttendeeIds.size,
                ),
              }
            : course,
        ),
      );
    }
    setBookings((rows) =>
      rows.map((row) =>
        row.id === booking.id
          ? {
              ...row,
              status: result.data.status || (result.data.fullyRefunded ? "refunded" : "partially_refunded"),
              refundedMinor:
                Number(row.refundedMinor || 0) +
                Number(result.data.amountMinor || 0),
              activeAttendeeCount: result.data.activeAttendeeCount,
              attendees: (row.attendees || []).map((attendee) =>
                refundedAttendeeIds.has(attendee.accountId)
                  ? { ...attendee, status: "refunded" }
                  : attendee,
              ),
              extras: (row.extras || []).map((extra) => {
                if (selection.type === "booking") return { ...extra, quantity: 0 };
                if (selection.type !== "extra"
                  || extra.id !== selection.extraId
                  || (extra.attendeeAccountId || null) !== (selection.attendeeAccountId || null)) return extra;
                return { ...extra, quantity: Math.max(0, Number(extra.quantity || 0) - Number(selection.quantity || 1)) };
              }),
            }
          : row,
      ),
    );
    setMessage(
      `Refunded ${pounds(result.data.amountMinor)} ${draft.currency}.`,
    );
  };
  const sections = [
    ["details", "Details & audience"],
    ["sessions", "Sessions"],
    ["pricing", "Tickets & extras"],
    ["booking", "Booking form"],
    ["content", "Documents & messages"],
    ["participants", "Participants & operations"],
    ["lifecycle", "Lifecycle & publish"],
  ];
  const closeWorkspace = () => onClose?.();
  if (loadingCourses) {
    return <div className="course-workspace">
      <header className="course-workspace__header">
        <div><h2>Courses</h2><p>Loading course setup and booking forms…</p></div>
        <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
      </header>
      <main className="course-library">
        <div className="course-library__empty" role="status"><h3>Loading courses…</h3><p>Preparing the complete course workspace.</p></div>
      </main>
    </div>;
  }
  if (workspaceView === "list") {
    return <div className="course-workspace">
      <header className="course-workspace__header">
        <div><h2>Courses</h2><p>Manage courses, bookings and attendance.</p></div>
        <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
      </header>
      <main className="course-library">
        <div className="course-library__toolbar">
          <label className="course-library__search">Search courses<input className="input" type="search" value={courseSearch} onChange={(event) => setCourseSearch(event.target.value)} placeholder="Course name" /></label>
          <label>Status<select className="input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All statuses</option><option value="draft">Draft</option><option value="published">Published</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></label>
          <button type="button" className="button button-primary" onClick={() => { setCopyCourseId(""); setWorkspaceView("create"); }}>Create course</button>
        </div>
        {message && <div className="course-workspace__message" role="status">{message}</div>}
        {loadingCourses ? <div className="course-library__empty" role="status"><h3>Loading courses…</h3><p>Fetching the course list. Participant totals will appear shortly.</p></div> : filteredCourses.length === 0 ? <div className="course-library__empty"><h3>No courses found</h3><p>Create a course or adjust the filters.</p></div> : <div className="course-library__grid">
          {filteredCourses.map((course) => {
            const editable = course.editorDraft || course;
            const nextSession = (editable.sessions || []).find((session) => session.start && new Date(session.start) >= new Date()) || editable.sessions?.[0];
            const price = editable.ticketTypes?.length ? Math.min(...editable.ticketTypes.map((ticket) => priceForTicket(ticket).priceMinor)) : null;
            return <button key={course.id} type="button" className="course-library__item" onClick={() => setEditorState(course)}>
              <span className={`course-status course-status--${course.status || "draft"}`}>{course.status || "draft"}</span>
              <strong>{course.name || "Untitled course"}</strong>
              <span>{nextSession?.start ? `Next: ${new Date(nextSession.start).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : "No sessions scheduled"}</span>
              <span>{course.activeParticipantCount == null ? "Loading enrolment total" : `${Number(course.activeParticipantCount)} / ${editable.capacity || 0} enrolled`}{price != null ? ` · from ${pounds(price)} ${editable.currency || "GBP"}` : ""}</span>
              {course.hasUnpublishedChanges && <span className="course-library__draft-note">Unpublished changes</span>}
            </button>;
          })}
        </div>}
      </main>
    </div>;
  }
  if (workspaceView === "create") {
    return <div className="course-workspace">
      <header className="course-workspace__header">
        <div><h2>New course</h2><p>Start fresh or copy an existing course.</p></div>
        <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
      </header>
      <main className="course-create">
        <section className="course-create__option">
          <div><h3>Start from scratch</h3><p>Add dates, tickets and booking details to a blank course.</p></div>
          <button type="button" className="button button-primary" onClick={() => setEditorState(null)}>Create blank course</button>
        </section>
        <section className="course-create__option">
          <div><h3>Copy an existing course</h3><p>Copies the setup and questions, but never bookings or participants.</p></div>
          <label>
            Course to copy
            <select className="input" value={copyCourseId} onChange={(event) => setCopyCourseId(event.target.value)}>
              <option value="">Choose a course</option>
              {courses.map((course) => <option key={course.id} value={course.id}>{course.name || "Untitled course"} · {course.status || "draft"}</option>)}
            </select>
          </label>
          <p className="text-muted">Session dates are copied for convenience, but every date must be moved into the future before publishing.</p>
          <button type="button" className="button button-primary" disabled={!copyCourseId} onClick={startCopiedCourse}>Copy and edit course</button>
        </section>
        <button type="button" className="button button-secondary course-create__back" onClick={() => setWorkspaceView("list")}>Back to courses</button>
      </main>
    </div>;
  }
  return (
    <div className="course-workspace course-workspace--editing">
      <header className="course-workspace__header">
        <div><button type="button" className="course-workspace__back" onClick={returnToList}>← Courses</button><h2>{draft.name || "New course"}</h2><p><span className={`course-status course-status--${liveStatus}`}>{liveStatus}</span>{autosaveStatus === "saving" ? " · Saving…" : autosaveStatus === "pending" ? " · Autosave pending…" : autosaveStatus === "error" ? " · Autosave needs attention" : " · Draft saved"}</p></div>
        <button type="button" className="button button-secondary" onClick={closeWorkspace}>Close</button>
      </header>
      <div className="course-workspace__body">
        <aside className="course-workspace__nav" aria-label="Course editor sections">{sections.map(([id, label]) => <button key={id} type="button" className={tab === id ? "is-active" : ""} aria-current={tab === id ? "step" : undefined} onClick={() => setTab(id)}><span>{label}</span>{validationErrors[id] && <span className="course-workspace__error-dot" aria-label="Needs attention">!</span>}</button>)}</aside>
        <main
          className="course-editor"
          onFocusCapture={(event) => {
            if (event.target.matches?.("input, textarea, select")) setIsEditingField(true);
          }}
          onBlurCapture={(event) => {
            const next = event.relatedTarget;
            if (!next || !event.currentTarget.contains(next) || !next.matches?.("input, textarea, select")) setIsEditingField(false);
          }}
        >
          <label className="course-workspace__mobile-nav">Course section<select className="input" value={tab} onChange={(event) => setTab(event.target.value)}>{sections.map(([id, label]) => <option key={id} value={id}>{label}{validationErrors[id] ? " — needs attention" : ""}</option>)}</select></label>
          {validationErrors[tab] && <div className="course-workspace__validation" role="alert">{validationErrors[tab]}</div>}
          {message && <div className="course-workspace__message" role="status">{message}</div>}
      {tab === "details" && (
        <div className="course-editor__stack">
          <div className="form-grid">
            <label>
              Course name
              <input
                className="input"
                value={draft.name}
                onChange={(event) => patch("name", event.target.value)}
              />
            </label>
            <label>
              Booking audience
              <select
                className="input"
                value={draft.audience}
                onChange={(event) => patch("audience", event.target.value)}
              >
                <option value="everyone">Members and non-members</option>
                <option value="members">Members only</option>
                <option value="non_members">Non-members only</option>
              </select>
            </label>
            <label>
              Overall capacity
              <input
                className="input"
                type="number"
                min="1"
                value={draft.capacity}
                onChange={(event) => patch("capacity", event.target.value)}
              />
            </label>
            <label>
              Currency
              <input
                className="input"
                value={draft.currency}
                maxLength="3"
                onChange={(event) =>
                  patch("currency", event.target.value.toUpperCase())
                }
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              Description
              <textarea
                className="input"
                rows="5"
                value={draft.description}
                onChange={(event) => patch("description", event.target.value)}
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={draft.waitlist?.enabled === true}
                onChange={(event) =>
                  patch("waitlist", {
                    ...draft.waitlist,
                    enabled: event.target.checked,
                  })
                }
              />{" "}
              Ordered waitlist
            </label>
            {draft.waitlist?.enabled && (
              <label>
                Offer lifetime (hours)
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={draft.waitlist.offerHours}
                  onChange={(event) =>
                    patch("waitlist", {
                      ...draft.waitlist,
                      offerHours: event.target.value,
                    })
                  }
                />
              </label>
            )}
          </div>
          {draft.audience !== "members" && (
            <section className="course-external-access">
              <div>
                <h3>External participant access</h3>
                <p className="text-muted">
                  People joining through this course receive course-only club access, not paid membership. Choose whether their dashboard is limited while they are enrolled.
                </p>
              </div>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={draft.restrictActiveCourseCards}
                  onChange={(event) =>
                    patch("restrictActiveCourseCards", event.target.checked)
                  }
                />{" "}
                Limit the club modules non-member participants can access
              </label>
              {draft.restrictActiveCourseCards ? (
                <DashboardCardPicker
                  selected={draft.activeCourseVisibleCardIds || []}
                  metadata={teamMetadata}
                  onChange={(activeCourseVisibleCardIds) =>
                    patch("activeCourseVisibleCardIds", activeCourseVisibleCardIds)
                  }
                />
              ) : (
                <p className="course-external-access__warning">
                  External participants will be able to use the normal member dashboard modules enabled for this club.
                </p>
              )}
            </section>
          )}
        </div>
      )}
      {tab === "sessions" && (
        <div className="course-editor__stack">
          {draft.sessions.map((session, index) => (
            <fieldset key={session.id} className="card">
              <legend>Session {index + 1}</legend>
              <button
                type="button"
                className="course-session-copy-toggle"
                aria-expanded={openSessionCopyId === session.id}
                aria-controls={`course-session-copy-${index}`}
                title="Copy this session"
                onClick={() => setOpenSessionCopyId((current) => current === session.id ? "" : session.id)}
              >
                <span aria-hidden="true">⧉</span>
                <span>Copy</span>
              </button>
              <div className="form-grid course-session-time-grid">
                <label>
                  Name
                  <input
                    className="input"
                    value={session.name}
                    onChange={(event) =>
                      patchList("sessions", index, { name: event.target.value })
                    }
                  />
                </label>
                <label>
                  Starts
                  <input
                    className="input"
                    type="datetime-local"
                    value={session.start?.slice(0, 16) || ""}
                    onChange={(event) =>
                      patchList("sessions", index, courseSessionTimesForStart(session, event.target.value))
                    }
                  />
                </label>
                <label>
                  Ends
                  <input
                    className="input"
                    type="datetime-local"
                    value={session.end?.slice(0, 16) || ""}
                    onChange={(event) =>
                      patchList("sessions", index, { end: event.target.value })
                    }
                  />
                </label>
              </div>
              <div className="course-session-location">
                <LocationFields
                  form={session}
                  locations={locations}
                  onChange={(event) => patchSessionLocation(index, event)}
                  allowSaveLocation={false}
                  idPrefix={`course-session-${session.id}-`}
                />
                <p className="text-muted">Saved locations are shared with Events and managed from Event setup.</p>
              </div>
              <CourseBoatReservations
                teamId={teamId}
                courseType="generic"
                session={session}
                onChange={(assetReservations) => patchList("sessions", index, { assetReservations })}
              />
              {openSessionCopyId === session.id && <div className="course-session-copy" id={`course-session-copy-${index}`}>
                <strong>Copy this session</strong>
                <div className="course-session-copy__controls">
                  <label>
                    Number of copies
                    <input
                      className="input"
                      type="number"
                      min="1"
                      max="52"
                      value={sessionCopyOption(session.id).count}
                      onChange={(event) => patchSessionCopyOption(session.id, { count: event.target.value })}
                    />
                  </label>
                  <label>
                    Every
                    <input
                      className="input"
                      type="number"
                      min="1"
                      max="52"
                      value={sessionCopyOption(session.id).every}
                      onChange={(event) => patchSessionCopyOption(session.id, { every: event.target.value })}
                    />
                  </label>
                  <label>
                    Interval
                    <select
                      className="input"
                      value={sessionCopyOption(session.id).unit}
                      onChange={(event) => patchSessionCopyOption(session.id, { unit: event.target.value })}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                  </label>
                  <button type="button" className="button button-secondary" disabled={!session.start || !session.end} onClick={() => copySession(index)}>
                    Copy session
                  </button>
                </div>
                <p className="text-muted">Copies keep this session's name, duration and location. You can adjust each copy before publishing.</p>
              </div>}
              <button
                type="button"
                className="button button-danger"
                onClick={() =>
                  patch(
                    "sessions",
                    draft.sessions.filter(
                      (_, itemIndex) => itemIndex !== index,
                    ),
                  )
                }
              >
                Remove session
              </button>
            </fieldset>
          ))}
          <button
            type="button"
            className="button button-secondary"
            onClick={() =>
              patch("sessions", [...draft.sessions, emptySession()])
            }
          >
            Add session
          </button>
        </div>
      )}
      {tab === "pricing" && (
        <div className="course-editor__stack">
          <h3>Ticket types</h3>
          {draft.ticketTypes.map((ticket, index) => {
            const timing = courseTicketTimingConfig(ticket);
            return <fieldset key={ticket.id} className="card">
              <legend>{ticket.name || `Ticket type ${index + 1}`}</legend>
              <div className="form-grid">
                <label>
                  Name
                  <input
                    className="input"
                    value={ticket.name}
                    onChange={(event) =>
                      patchList("ticketTypes", index, {
                        name: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Standard price ({draft.currency})
                  <input
                    className="input"
                    inputMode="decimal"
                    value={moneyInputValue(`ticket:${ticket.id}:standard`, timing.standardPriceMinor)}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => updateMoneyInput(`ticket:${ticket.id}:standard`, event.target.value, (priceMinor) => patchTicketTiming(index, { standardPriceMinor: priceMinor }))}
                    onBlur={() => finishMoneyInput(`ticket:${ticket.id}:standard`)}
                  />
                </label>
                <label>
                  Eligibility
                  <select
                    className="input"
                    value={ticket.eligibility}
                    onChange={(event) =>
                      patchList("ticketTypes", index, {
                        eligibility: event.target.value,
                      })
                    }
                  >
                    <option value="everyone">Everyone</option>
                    <option value="members">Members</option>
                    <option value="non_members">Non-members</option>
                  </select>
                </label>
                <label>
                  Ticket capacity (optional)
                  <input
                    className="input"
                    type="number"
                    min="1"
                    value={ticket.capacity ?? ""}
                    onChange={(event) =>
                      patchList("ticketTypes", index, {
                        capacity: event.target.value,
                      })
                    }
                  />
                </label>
                {ticket.eligibility === "members" && <TagPicker
                  tags={teamTags}
                  selected={ticket.memberTagNames || []}
                  onChange={(memberTagNames) => patchList("ticketTypes", index, { memberTagNames })}
                  label="Eligible member tags"
                  emptyText="No tags selected — available to any member."
                  resultsLabel="Matching member tags"
                />}
                <div className="course-ticket-rules">
                  <div><strong>Booking availability</strong><p className="text-muted">Choose simple rules; exact dates are only needed for a custom schedule.</p></div>
                  <div className="course-ticket-rules__grid">
                    <label>Bookings open<select className="input" value={timing.availability.openMode} onChange={(event) => patchTicketTiming(index, { availability: { openMode: event.target.value } })}><option value="publication">As soon as the course is published</option><option value="relative">A set time before the first session</option><option value="specific">On a specific date and time</option></select></label>
                    <label>Bookings close<select className="input" value={timing.availability.closeMode} onChange={(event) => patchTicketTiming(index, { availability: { closeMode: event.target.value } })}><option value="first_session_start">At the start of the first session</option><option value="course_end">Keep open for the whole course</option><option value="relative">A set time before the first session</option><option value="specific">On a specific date and time</option><option value="never">Do not close automatically</option></select></label>
                    {timing.availability.openMode === "relative" && <div className="course-ticket-rules__offset"><label>Open<input className="input" type="number" min="1" value={timing.availability.openOffset} onChange={(event) => patchTicketTiming(index, { availability: { openOffset: event.target.value } })} /></label><label>Before first session<select className="input" value={timing.availability.openUnit} onChange={(event) => patchTicketTiming(index, { availability: { openUnit: event.target.value } })}><option value="hours">Hours</option><option value="days">Days</option><option value="weeks">Weeks</option></select></label></div>}
                    {timing.availability.openMode === "specific" && <label>Opening date and time<input className="input" type="datetime-local" value={timing.availability.openAt?.slice(0, 16) || ""} onChange={(event) => patchTicketTiming(index, { availability: { openAt: event.target.value } })} /></label>}
                    {timing.availability.closeMode === "relative" && <div className="course-ticket-rules__offset"><label>Close<input className="input" type="number" min="1" value={timing.availability.closeOffset} onChange={(event) => patchTicketTiming(index, { availability: { closeOffset: event.target.value } })} /></label><label>Before first session<select className="input" value={timing.availability.closeUnit} onChange={(event) => patchTicketTiming(index, { availability: { closeUnit: event.target.value } })}><option value="hours">Hours</option><option value="days">Days</option><option value="weeks">Weeks</option></select></label></div>}
                    {timing.availability.closeMode === "specific" && <label>Closing date and time<input className="input" type="datetime-local" value={timing.availability.closeAt?.slice(0, 16) || ""} onChange={(event) => patchTicketTiming(index, { availability: { closeAt: event.target.value } })} /></label>}
                  </div>
                </div>
                <div className="course-ticket-rules">
                  <div><strong>Early-bird price</strong><p className="text-muted">Optionally charge a lower price until a clear deadline.</p></div>
                  <div className="course-ticket-rules__grid">
                    <label>Early bird<select className="input" value={timing.earlyBird.mode} onChange={(event) => patchTicketTiming(index, { earlyBird: { mode: event.target.value } })}><option value="none">No early-bird price</option><option value="relative">End a set time before the first session</option><option value="specific">End on a specific date and time</option></select></label>
                    {timing.earlyBird.mode !== "none" && <label>Early-bird price ({draft.currency})<input className="input" inputMode="decimal" value={moneyInputValue(`ticket:${ticket.id}:early`, timing.earlyBird.priceMinor)} onFocus={(event) => event.target.select()} onChange={(event) => updateMoneyInput(`ticket:${ticket.id}:early`, event.target.value, (priceMinor) => patchTicketTiming(index, { earlyBird: { priceMinor } }))} onBlur={() => finishMoneyInput(`ticket:${ticket.id}:early`)} /></label>}
                    {timing.earlyBird.mode === "relative" && <div className="course-ticket-rules__offset"><label>End<input className="input" type="number" min="1" value={timing.earlyBird.offset} onChange={(event) => patchTicketTiming(index, { earlyBird: { offset: event.target.value } })} /></label><label>Before first session<select className="input" value={timing.earlyBird.unit} onChange={(event) => patchTicketTiming(index, { earlyBird: { unit: event.target.value } })}><option value="hours">Hours</option><option value="days">Days</option><option value="weeks">Weeks</option></select></label></div>}
                    {timing.earlyBird.mode === "specific" && <label>Early bird ends<input className="input" type="datetime-local" value={timing.earlyBird.endsAt?.slice(0, 16) || ""} onChange={(event) => patchTicketTiming(index, { earlyBird: { endsAt: event.target.value } })} /></label>}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="button button-danger"
                onClick={() =>
                  patch(
                    "ticketTypes",
                    draft.ticketTypes.filter(
                      (_, itemIndex) => itemIndex !== index,
                    ),
                  )
                }
              >
                Remove ticket
              </button>
            </fieldset>;
          })}
          <button
            type="button"
            className="button button-secondary"
            onClick={() =>
              patch("ticketTypes", [...draft.ticketTypes, emptyTicket()])
            }
          >
            Add ticket type
          </button>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={draft.ticketingReviewed === true}
              onChange={(event) => patch("ticketingReviewed", event.target.checked)}
            />{" "}
            I have reviewed ticket prices, eligibility, capacity and booking availability
          </label>
          <h3>Paid extras</h3>
          {draft.extras.map((extra, index) => (
            <fieldset key={extra.id} className="card">
              <legend>{extra.name || `Paid extra ${index + 1}`}</legend>
              <div className="form-grid">
                <label>
                  Name
                  <input
                    className="input"
                    value={extra.name}
                    onChange={(event) =>
                      patchList("extras", index, { name: event.target.value })
                    }
                  />
                </label>
                <label>
                  Price
                  <input
                    className="input"
                    value={moneyInputValue(`extra:${extra.id}:price`, extra.priceMinor)}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => updateMoneyInput(`extra:${extra.id}:price`, event.target.value, (priceMinor) => patchList("extras", index, { priceMinor }))}
                    onBlur={() => finishMoneyInput(`extra:${extra.id}:price`)}
                  />
                </label>
                <label>
                  Apply
                  <select
                    className="input"
                    value={extra.scope}
                    onChange={(event) =>
                      patchList("extras", index, { scope: event.target.value })
                    }
                  >
                    <option value="attendee">Per attendee</option>
                    <option value="booking">Per booking</option>
                  </select>
                </label>
                <label>
                  Maximum quantity
                  <input
                    className="input"
                    type="number"
                    min="1"
                    value={extra.maxQuantity || 1}
                    onChange={(event) =>
                      patchList("extras", index, {
                        maxQuantity: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Capacity (optional)
                  <input
                    className="input"
                    type="number"
                    value={extra.capacity ?? ""}
                    onChange={(event) =>
                      patchList("extras", index, {
                        capacity: event.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="button button-danger"
                onClick={() =>
                  patch(
                    "extras",
                    draft.extras.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
              >
                Remove extra
              </button>
            </fieldset>
          ))}
          <button
            type="button"
            className="button button-secondary"
            onClick={() => patch("extras", [...draft.extras, emptyExtra()])}
          >
            Add extra
          </button>
        </div>
      )}
      {tab === "booking" && (
        <div className="course-booking-form">
          <div className="course-booking-form__status">
            <div><h3>Booking questions and declarations</h3><p>Owned by this course. Existing bookings always retain the version they completed.</p></div>
            <span className="course-status">{formNeedsPublication || formIsDirty ? `Draft changes${selectedForm?.latestVersionNumber ? ` · live v${selectedForm.latestVersionNumber}` : ""}` : selectedForm?.latestVersionNumber ? `Published v${selectedForm.latestVersionNumber}` : formDraftId ? "Draft" : "Not configured"}</span>
          </div>
          <label>
            Booking information
            <select
              className="input"
              value={draft.bookingFormMode || ""}
              onChange={(event) => patch("bookingFormMode", event.target.value)}
            >
              <option value="">Choose an option</option>
              <option value="questions">Collect booking questions or declarations</option>
              <option value="none">No additional booking questions are needed</option>
            </select>
          </label>
          {draft.bookingFormMode === "none" ? (
            <div className="course-booking-form__readonly">
              <strong>No additional form</strong>
              <p>Checkout will still collect the attendee, purchaser and payment details required for the booking.</p>
            </div>
          ) : draft.bookingFormMode === "questions" ? <>
          {selectedForm && (
            <p className="text-muted">
              New bookings use version {selectedForm.latestVersionNumber};
              existing bookings retain their original version.
            </p>
          )}
          {canEditForms && copySources.length > 0 && !formDraftId && <div className="course-booking-form__copy"><label>Start from an existing course or event<select className="input" value={copySourceFormId} onChange={(event) => setCopySourceFormId(event.target.value)}><option value="">Choose a form to copy</option>{copySources.map((form) => <option key={form.id} value={form.id}>{form.name}{form.ownerType ? ` · ${form.ownerType}` : " · legacy"}</option>)}</select></label><button type="button" className="button button-secondary" disabled={!copySourceFormId || saving} onClick={copySelectedForm}>Copy into this course</button></div>}
          {!canEditForms ? <div className="course-booking-form__readonly"><strong>Form editing is read-only</strong><p>You need the Booking Forms permission to change questions or publish a new version.</p>{(selectedForm?.draft?.fields || formDraft.fields || []).map((field) => <div key={field.key}><strong>{field.label}</strong><span>{field.scope === "booking" ? "Once per booking" : "For each attendee"}{field.required ? " · required" : ""}{field.sensitive ? " · protected" : ""}</span></div>)}</div> : <>
          <h3>Edit this course's form</h3>
          <label>
            Form name
            <input
              className="input"
              value={formDraft.name}
              onChange={(event) =>
                setFormDraft((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
          </label>
          {formDraft.fields.map((field, index) => (
            <fieldset key={field.key} className="card">
              <legend>{field.label || `Question ${index + 1}`}</legend>
              <div className="form-grid">
                <label>
                  Label
                  <input
                    className="input"
                    value={field.label}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, label: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  />
                </label>
                <label>
                  Type
                  <select
                    className="input"
                    value={field.type}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, type: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  >
                    {BOOKING_FIELD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Collect
                  <select
                    className="input"
                    value={field.scope}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, scope: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  >
                    <option value="attendee">For every attendee</option>
                    <option value="booking">Once per booking</option>
                  </select>
                </label>
                <label>
                  Help text
                  <input
                    className="input"
                    value={field.helpText}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, helpText: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  />
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, required: event.target.checked }
                            : item,
                        ),
                      }))
                    }
                  />{" "}
                  Required
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={field.sensitive}
                    onChange={(event) =>
                      setFormDraft((current) => ({
                        ...current,
                        fields: current.fields.map((item, i) =>
                          i === index
                            ? { ...item, sensitive: event.target.checked }
                            : item,
                        ),
                      }))
                    }
                  />{" "}
                  Sensitive
                </label>
                {!field.sensitive &&
                  field.type !== "confirmation" &&
                  field.scope === "attendee" && (
                    <label>
                      Optional profile prefill
                      <select
                        className="input"
                        value={field.profileBinding || ""}
                        onChange={(event) =>
                          setFormDraft((current) => ({
                            ...current,
                            fields: current.fields.map((item, i) =>
                              i === index
                                ? {
                                    ...item,
                                    profileBinding: event.target.value,
                                  }
                                : item,
                            ),
                          }))
                        }
                      >
                        <option value="">Do not map to profile</option>
                        <option value="firstName">First name</option>
                        <option value="lastName">Last name</option>
                        <option value="displayName">Display name</option>
                        <option value="phone">Contact phone</option>
                        <option value="dob">Date of birth</option>
                      </select>
                    </label>
                  )}
                {["single_choice", "multiple_choice"].includes(field.type) && (
                  <label>
                    Choices
                    <input
                      className="input"
                      value={(field.options || []).join(", ")}
                      onChange={(event) =>
                        setFormDraft((current) => ({
                          ...current,
                          fields: current.fields.map((item, i) =>
                            i === index
                              ? {
                                  ...item,
                                  options: event.target.value
                                    .split(",")
                                    .map((option) => option.trim())
                                    .filter(Boolean),
                                }
                              : item,
                          ),
                        }))
                      }
                    />
                  </label>
                )}
                {field.type === "confirmation" && (
                  <>
                    <label>
                      Declaration version
                      <input
                        className="input"
                        value={field.declarationVersion}
                        onChange={(event) =>
                          setFormDraft((current) => ({
                            ...current,
                            fields: current.fields.map((item, i) =>
                              i === index
                                ? {
                                    ...item,
                                    declarationVersion: event.target.value,
                                  }
                                : item,
                            ),
                          }))
                        }
                      />
                    </label>
                    <label style={{ gridColumn: "1 / -1" }}>
                      Declaration wording
                      <textarea
                        className="input"
                        value={field.declarationWording}
                        onChange={(event) =>
                          setFormDraft((current) => ({
                            ...current,
                            fields: current.fields.map((item, i) =>
                              i === index
                                ? {
                                    ...item,
                                    declarationWording: event.target.value,
                                  }
                                : item,
                            ),
                          }))
                        }
                      />
                    </label>
                  </>
                )}
              </div>
              <div className="course-booking-form__field-actions">
                <button type="button" className="button button-secondary" disabled={index === 0} onClick={() => setFormDraft((current) => { const fields = [...current.fields]; [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]]; return { ...current, fields }; })}>Move up</button>
                <button type="button" className="button button-secondary" disabled={index === formDraft.fields.length - 1} onClick={() => setFormDraft((current) => { const fields = [...current.fields]; [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]]; return { ...current, fields }; })}>Move down</button>
                <button type="button" className="button button-secondary" onClick={() => setFormDraft((current) => ({ ...current, fields: [...current.fields.slice(0, index + 1), { ...field, key: uid("field"), label: `${field.label || "Question"} copy` }, ...current.fields.slice(index + 1)] }))}>Duplicate</button>
                <button type="button" className="button button-danger" onClick={() => setFormDraft((current) => ({ ...current, fields: current.fields.filter((_, i) => i !== index) }))}>Remove</button>
              </div>
            </fieldset>
          ))}
          <button
            type="button"
            className="button button-secondary"
            onClick={() =>
              setFormDraft((current) => ({
                ...current,
                fields: [...current.fields, emptyField()],
              }))
            }
          >
            Add field
          </button>
          <p className="text-muted">Draft changes save automatically and remain private. Publishing the course creates and attaches a new immutable form version.</p>
          </>}
          </> : (
            <p className="text-muted">Make an explicit choice before publishing so the course cannot accidentally go live without its intended safety questions or declarations.</p>
          )}
        </div>
      )}
      {tab === "content" && (
        <div className="card">
          <h3>Course documents</h3>
          <p className="text-muted">
            Link documents from the club library. Each document keeps its
            public, members-only or confirmed-bookers visibility. Removing a
            link never deletes the file.
          </p>
          {documents.length === 0 ? (
            <p>No club documents are available.</p>
          ) : (
            documents.map((document) => (
              <label key={document.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={linkedDocumentIds.includes(document.id)}
                  onChange={(event) =>
                    setLinkedDocumentIds((ids) =>
                      event.target.checked
                        ? [...ids, document.id]
                        : ids.filter((id) => id !== document.id),
                    )
                  }
                />
                <span>
                  {document.title}
                  <small>
                    {document.category ? `${document.category} · ` : ""}
                    {document.visibility.replace("_", " ")}
                  </small>
                </span>
              </label>
            ))
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="button button-primary"
              disabled={!courseId}
              onClick={() =>
                saveDocumentLinks().catch((error) => setMessage(error.message))
              }
            >
              Save document links
            </button>
            <button
              type="button"
              className="button button-secondary"
              disabled={!courseId}
              onClick={() => setShowDocumentManager(true)}
            >
              Upload or manage club documents
            </button>
          </div>
        </div>
      )}
      {tab === "content" && (
        <div className="card">
          <h3>Participant communications</h3>
          <p className="text-muted">
            Email confirmed booking contacts, including external purchasers and
            parents or proxies. Addresses are deduplicated and recipients are
            resolved again when a scheduled message is sent.
          </p>
          <button
            type="button"
            className="button button-primary"
            disabled={!courseId}
            onClick={() => setShowComposer(true)}
          >
            Compose course email
          </button>
        </div>
      )}
      {tab === "lifecycle" && (
        <div className="form-grid">
          <label>
            Protected answer retention after final session (days)
            <input
              className="input"
              type="number"
              min="1"
              value={draft.protectedAnswerRetentionDays}
              onChange={(event) =>
                patch("protectedAnswerRetentionDays", event.target.value)
              }
            />
          </label>
          <TagPicker
            tags={teamTags}
            selected={draft.completionTagNames || []}
            onChange={(completionTagNames) =>
              patch("completionTagNames", completionTagNames)
            }
            label="Tags applied after completion"
            emptyText="No completion tags selected."
            resultsLabel="Matching completion tags"
          />
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={draft.conversion?.enabled}
              onChange={(event) =>
                patch("conversion", {
                  ...draft.conversion,
                  enabled: event.target.checked,
                })
              }
            />{" "}
            Offer membership conversion
          </label>
          {draft.conversion?.enabled && (
            <>
              <label>
                Conversion starts
                <select
                  className="input"
                  value={draft.conversion.startsMode}
                  onChange={(event) =>
                    patch("conversion", {
                      ...draft.conversion,
                      startsMode: event.target.value,
                    })
                  }
                >
                  <option value="after_final_session">
                    After final session
                  </option>
                  <option value="manual_date">On a chosen date</option>
                </select>
              </label>
              {draft.conversion.startsMode === "manual_date" && (
                <label>
                  Date
                  <input
                    className="input"
                    type="datetime-local"
                    value={draft.conversion.startsAt?.slice(0, 16) || ""}
                    onChange={(event) =>
                      patch("conversion", {
                        ...draft.conversion,
                        startsAt: event.target.value,
                      })
                    }
                  />
                </label>
              )}
              <label>
                Membership price
                <select
                  className="input"
                  value={draft.conversion.pricingMode}
                  onChange={(event) =>
                    patch("conversion", {
                      ...draft.conversion,
                      pricingMode: event.target.value,
                    })
                  }
                >
                  <option value="full_price">Normal membership price</option>
                  <option value="credit_course_fee">
                    Credit retained base course ticket once
                  </option>
                </select>
              </label>
            </>
          )}
        </div>
      )}
      {tab === "participants" && (
        <div className="course-editor__stack">
          <div className="card course-operations-search">
            <label>
              Find a participant or booking
              <input
                className="input"
                type="search"
                value={operationsSearch}
                onChange={(event) => {
                  setOperationsSearch(event.target.value);
                  setAttendancePage(1);
                  setBookingPage(1);
                  setWaitlistPage(1);
                }}
                placeholder="Search by participant, ticket or status"
              />
            </label>
            <p className="text-muted">Filters attendance, bookings and the course waitlist.</p>
          </div>
          <div className="card">
            <h3>Exports and safety register</h3>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeSensitive}
                onChange={(event) => setIncludeSensitive(event.target.checked)}
              />{" "}
              Include protected fields (requires sensitive-answer permission and
              creates an audit record)
            </label>
            <label>
              CSV columns (comma separated)
              <input
                className="input"
                value={exportColumns}
                onChange={(event) => setExportColumns(event.target.value)}
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeCancelled}
                onChange={(event) => setIncludeCancelled(event.target.checked)}
              />{" "}
              Include cancelled/refunded attendees and mark their status
            </label>
            <label>
              Register layout
              <select
                className="input"
                value={registerLayout}
                onChange={(event) => setRegisterLayout(event.target.value)}
              >
                <option value="compact">Compact roster</option>
                <option value="participant_pages">
                  One participant per page
                </option>
              </select>
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="button button-secondary"
                disabled={!courseId}
                onClick={() =>
                  downloadCsv().catch((error) => setMessage(error.message))
                }
              >
                Download CSV
              </button>
              <button
                type="button"
                className="button button-secondary"
                disabled={!courseId || (!includeCancelled && confirmedParticipants.length === 0)}
                onClick={() =>
                  printRegister().catch((error) => setMessage(error.message))
                }
              >
                Print A4 register
              </button>
            </div>
          </div>
          <div className="card">
            <h3>Session attendance ({confirmedParticipants.length})</h3>
            {confirmedParticipants.length === 0 ? (
              <p className="text-muted">
                No paid or confirmed participants yet. Incomplete checkouts do
                not appear in attendance.
              </p>
            ) : matchingParticipants.length === 0 ? (
              <p className="text-muted">No participants match “{operationsSearch.trim()}”.</p>
            ) : !attendanceSession ? (
              <p className="text-muted">Add a session before recording attendance.</p>
            ) : (
              <div className="course-attendance">
                <label className="course-attendance__session">
                  Session to mark
                  <select
                    className="input"
                    value={attendanceSession.id}
                    onChange={(event) => setAttendanceSessionId(event.target.value)}
                  >
                    {draft.sessions.map((session, index) => (
                      <option key={session.id} value={session.id}>
                        {index + 1}. {session.name || "Untitled session"}
                        {session.start ? ` — ${new Date(session.start).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="course-attendance__summary">
                  <div>
                    <strong>{attendanceSession.name || "Untitled session"}</strong>
                    <span>
                      {attendanceSession.start
                        ? new Date(attendanceSession.start).toLocaleString([], { dateStyle: "full", timeStyle: "short" })
                        : "Date and time not set"}
                      {attendanceSession.locationName ? ` · ${attendanceSession.locationName}` : ""}
                    </span>
                  </div>
                  <a
                    className="button button-primary"
                    href={`/teams/${encodeURIComponent(teamId)}/courses/${encodeURIComponent(courseId)}/check-in?session=${encodeURIComponent(attendanceSession.id)}`}
                  >
                    Open QR check-in
                  </a>
                </div>
                <div className="course-attendance__roster">
                  {attendancePagination.rows.map((participant) => (
                    <label key={participant.id} className="course-attendance__participant">
                      <span>
                        <strong>{participant.attendeeName || "Participant"}</strong>
                        <small>{participant.ticketName || "Course participant"}</small>
                      </span>
                      <select
                        className="input"
                        aria-label={`${participant.attendeeName || "Participant"} attendance`}
                        value={
                          participant.attendance?.[attendanceSession.id]?.status ||
                          "unknown"
                        }
                        onChange={async (event) => {
                          const status = event.target.value;
                          await updateAttendance({
                            teamId,
                            courseId,
                            sessionId: attendanceSession.id,
                            participantId: participant.id,
                            status,
                          });
                          setParticipants((rows) =>
                            rows.map((row) =>
                              row.id === participant.id
                                ? {
                                    ...row,
                                    attendance: {
                                      ...row.attendance,
                                      [attendanceSession.id]: { status },
                                    },
                                  }
                                : row,
                            ),
                          );
                        }}
                      >
                        <option value="unknown">Not marked</option>
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                        <option value="excused">Excused</option>
                      </select>
                    </label>
                  ))}
                </div>
                <CourseOperationsPager label="Attendance" total={matchingParticipants.length} pagination={attendancePagination} onPageChange={setAttendancePage} />
              </div>
            )}
          </div>
          <div className="card">
            <h3>Bookings and refunds ({completedBookings.length})</h3>
            {completedBookings.length === 0 ? (
              <p className="text-muted">No completed course bookings yet.</p>
            ) : matchingBookings.length === 0 ? (
              <p className="text-muted">No bookings match “{operationsSearch.trim()}”.</p>
            ) : bookingPagination.rows.map((booking) => {
              const remainingMinor = Math.max(
                0,
                Number(booking.totalMinor || 0) -
                  Number(booking.refundedMinor || 0),
              );
              const canRefund = ["paid", "partially_refunded"].includes(
                booking.status,
              ) && remainingMinor > 0 && Boolean(booking.payment?.paymentIntentId);
              return (
                <div
                  key={booking.id}
                  className="course-operation-row"
                >
                  <strong>
                    {booking.bookingContactName || "Booking contact"}
                  </strong>
                  <p>
                    {(booking.attendees || [])
                      .map((attendee) => attendee.name)
                      .join(", ")}{" "}
                    · {booking.status} ·{" "}
                    {pounds(remainingMinor)}{" "}
                    {booking.currency || draft.currency} remaining
                  </p>
                  {canRefund ? <details className="course-refund-options">
                    <summary>Refund options</summary>
                    <div className="course-operation-row__actions">
                    {(booking.attendees || [])
                      .filter((attendee) => attendee.status !== "refunded")
                      .map((attendee) => (
                        <button
                          key={attendee.accountId}
                          type="button"
                          className="button button-secondary"
                          onClick={() =>
                            runRefund(
                              booking,
                              {
                                type: "attendee",
                                attendeeAccountId: attendee.accountId,
                              },
                              `${attendee.name}'s ticket`,
                            )
                          }
                        >{`Refund ${attendee.name}'s ticket`}</button>
                      ))}
                    {(booking.extras || [])
                      .filter((extra) => Number(extra.quantity || 0) > 0)
                      .map((extra) => (
                        <button
                          key={`${extra.id}-${extra.attendeeAccountId || "booking"}`}
                          type="button"
                          className="button button-secondary"
                          onClick={() =>
                            runRefund(
                              booking,
                              {
                                type: "extra",
                                extraId: extra.id,
                                attendeeAccountId:
                                  extra.attendeeAccountId || null,
                                quantity: 1,
                              },
                              `one ${extra.name}`,
                            )
                          }
                        >{`Refund one ${extra.name}`}</button>
                      ))}
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={["refunded", "cancelled"].includes(
                        booking.status,
                      )}
                      onClick={() =>
                        runRefund(
                          booking,
                          { type: "booking" },
                          "the remaining booking",
                        )
                      }
                    >
                      Refund remaining booking
                    </button>
                    </div>
                  </details> : (
                    <p className="text-muted">
                      {booking.status === "refunded"
                        ? "This booking has been fully refunded."
                        : "No refundable card payment remains for this booking."}
                    </p>
                  )}
                </div>
              );
            })}
            <CourseOperationsPager label="Bookings and refunds" total={matchingBookings.length} pagination={bookingPagination} onPageChange={setBookingPage} />
            {activePaymentHolds.length > 0 && (
              <p className="course-payment-holds">
                <strong>
                  {activePaymentHoldPlaces}
                  {" "}temporary {activePaymentHoldPlaces === 1 ? "place is" : "places are"} reserved
                </strong>
                {" "}while payment is completed. Unpaid reservations clear automatically after 10 minutes.
              </p>
            )}
          </div>
          <div className="card">
            <h3>Course waitlist ({activeWaitlist.length})</h3>
            {activeWaitlist.length === 0 ? (
              <p className="text-muted">Nobody is waiting.</p>
            ) : matchingWaitlist.length === 0 ? (
              <p className="text-muted">No waitlist entries match “{operationsSearch.trim()}”.</p>
            ) : (
              <div className="course-waitlist">
                {waitlistPagination.rows.map((entry) => (
                  <div
                    key={entry.id}
                    className="course-waitlist__row"
                  >
                    <span>
                      <strong>{entry.accountName || "Waitlisted account"}</strong>
                      <small>Position {activeWaitlist.findIndex((row) => row.id === entry.id) + 1} · {entry.status === "offered" ? "Offer sent" : "Waiting"}</small>
                    </span>
                    {entry.status === "queued" && activeWaitlist[0]?.id === entry.id && (
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={async () => {
                          const result = await releaseWaitlistOffer({
                            teamId,
                            courseId,
                            entryId: entry.id,
                          });
                          setWaitlist((rows) =>
                            rows.map((row) =>
                              row.id === entry.id
                                ? {
                                    ...row,
                                    status: "offered",
                                    offerExpiresAt: result.data.expiresAt,
                                  }
                                : row,
                            ),
                          );
                        }}
                      >
                        Release timed offer
                      </button>
                    )}
                  </div>
                ))}
                <CourseOperationsPager label="Course waitlist" total={matchingWaitlist.length} pagination={waitlistPagination} onPageChange={setWaitlistPage} />
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "lifecycle" && (
        <div className="card">
          <h3>{draft.name || "Untitled course"}</h3>
          <p>
            {draft.sessions.length} sessions · {draft.ticketTypes.length} ticket
            types · capacity {draft.capacity}
          </p>
          <label>
            Publication status
            <select
              className="input"
              value={draft.status}
              onChange={(event) => patch("status", event.target.value)}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <p className="text-muted">
            Publishing creates the public course listing and synchronises every
            session into Events.
          </p>
        </div>
      )}
          <footer className="course-workspace__footer">
            <button type="button" className="button button-secondary" onClick={returnToList}>Back to courses</button>
            <div className="course-workspace__footer-actions"><span className={`course-autosave-status course-autosave-status--${autosaveStatus}`} role="status">{autosaveStatus === "saving" ? "Saving draft…" : autosaveStatus === "pending" ? "Changes save automatically" : autosaveStatus === "error" ? "Draft not saved" : "Draft saved"}</span>{autosaveStatus === "error" && <button type="button" className="button button-secondary" disabled={saving} onClick={() => saveDraft()}>Retry save</button>}<button type="button" className="button button-primary" disabled={saving} onClick={publishCourse}>{saving ? "Working…" : liveStatus === "published" ? "Publish update" : "Publish course"}</button></div>
          </footer>
        </main>
      </div>
      <EmailComposerModal
        isOpen={showComposer}
        onClose={() => setShowComposer(false)}
        teamId={teamId}
        tags={[]}
        members={[]}
        defaultAudienceMode="booking"
        defaultBookingAudience={
          courseId ? { sourceType: "course", sourceId: courseId } : null
        }
        bookingAudienceOptions={
          courseId
            ? [
                {
                  label: `${draft.name || "Course"} — all confirmed bookers`,
                  value: { sourceType: "course", sourceId: courseId },
                },
                ...(draft.sessions || []).map((session) => ({
                  label: `${draft.name || "Course"} — ${session.name || "session"}`,
                  value: {
                    sourceType: "course",
                    sourceId: courseId,
                    sessionId: session.id,
                  },
                })),
              ]
            : []
        }
      />
      <EditDocumentsForm
        isOpen={showDocumentManager}
        teamId={teamId}
        defaultAssociation={
          courseId ? { sourceType: "course", sourceId: courseId } : null
        }
        onClose={() => setShowDocumentManager(false)}
        onSave={() => setDocumentRefresh((value) => value + 1)}
      />
      {stripeGuardDialog}
    </div>
  );
}
